import React from "react";
import { useLang, useSettings, cldUrl, assetUrl } from "../lib/providers";
import { Lock, MapPin, Instagram, Facebook, Youtube, Linkedin, Twitter, Music2 } from "lucide-react";
import NewsletterSignup from "./NewsletterSignup";

const SOCIALS = [
  { key: "instagram", Icon: Instagram, color: "hover:text-[#E1306C]" },
  { key: "facebook", Icon: Facebook, color: "hover:text-[#1877F2]" },
  { key: "tiktok", Icon: Music2, color: "hover:text-[#69C9D0]" },
  { key: "youtube", Icon: Youtube, color: "hover:text-[#FF0000]" },
  { key: "twitter", Icon: Twitter, color: "hover:text-white" },
  { key: "linkedin", Icon: Linkedin, color: "hover:text-[#0A66C2]" },
];

export default function Footer({ onAdminClick }) {
  const { lang, t } = useLang();
  const { settings } = useSettings();
  if (!settings.footer_enabled) return null;
  const tagline = lang === "ar" ? settings.footer_tagline_ar : settings.footer_tagline_fr;
  const payments = (settings.payment_methods || []).filter((p) => p.enabled);
  const carriers = (settings.carriers || []).filter((c) => c.enabled);
  const newsletterOn = settings.newsletter_enabled !== false;
  const address = lang === "ar" ? (settings.company_address_ar || settings.company_address_fr) : (settings.company_address_fr || settings.company_address_ar);
  const country = lang === "ar" ? settings.company_country_ar : settings.company_country_fr;
  const hasAddress = !!(address || settings.company_city);
  const activeSocials = SOCIALS.filter((s) => settings[`social_${s.key}_url`]);

  return (
    <footer id="contact" className="bg-themed-deep border-t border-white/10 py-12 sm:py-16 mt-0 relative" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <img src={cldUrl(settings.logo_url, { w: 280 })} alt={settings.site_name} className="h-14 w-auto" loading="lazy" decoding="async" />
            <p className="mt-4 text-sm text-white/60 max-w-xs">{tagline}</p>
            {settings.footer_extra_text && (
              <p className="mt-2 text-xs text-white/40 max-w-xs whitespace-pre-line">{settings.footer_extra_text}</p>
            )}
            <a href="/guides" className="mt-4 inline-block text-sm text-white/80 hover:text-neon transition-colors" data-testid="footer-guides-link">
              {lang === "ar" ? "أدلة عملية" : "Guides pratiques"}
            </a>
            {newsletterOn && (
              <div className="mt-6 max-w-xs">
                <NewsletterSignup variant="footer" source="footer" />
              </div>
            )}
          </div>
          {settings.footer_show_contacts && (
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-3">{t.footer.contact}</div>
              <ul className="space-y-2 text-sm">
                {settings.whatsapp_dz && (
                  <li>
                    <a href={`https://wa.me/${(settings.whatsapp_dz||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white/80 hover:text-neon transition-colors" data-testid="footer-wa-dz">
                      <span className="text-base">🇩🇿</span>
                      <span>{lang === "ar" ? settings.contact_whatsapp_dz_label_ar || "واتساب الجزائر" : settings.contact_whatsapp_dz_label_fr || "WhatsApp Algérie"}</span>
                    </a>
                  </li>
                )}
                {settings.whatsapp_fr && (
                  <li>
                    <a href={`https://wa.me/${(settings.whatsapp_fr||"").replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-white/80 hover:text-neon transition-colors" data-testid="footer-wa-fr">
                      <span className="text-base">🇫🇷</span>
                      <span>{lang === "ar" ? settings.contact_whatsapp_fr_label_ar || "واتساب فرنسا" : settings.contact_whatsapp_fr_label_fr || "WhatsApp France"}</span>
                    </a>
                  </li>
                )}
                {settings.contact_email && (
                  <li>
                    <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 text-white/80 hover:text-neon transition-colors" data-testid="footer-email">
                      <span className="text-base">✉️</span>
                      <span>Email</span>
                    </a>
                  </li>
                )}
                {hasAddress && (
                  <li className="pt-1" data-testid="footer-address">
                    <a
                      href={settings.company_map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address || ""} ${settings.company_city || ""}`)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-start gap-2 text-white/80 hover:text-neon transition-colors"
                    >
                      <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                      <span className="text-xs leading-snug whitespace-pre-line">
                        {address}{address && (settings.company_city || country) ? "\n" : ""}
                        {[settings.company_city, country].filter(Boolean).join(", ")}
                      </span>
                    </a>
                  </li>
                )}
              </ul>
              {activeSocials.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="footer-socials">
                  {activeSocials.map(({ key, Icon, color }) => {
                    const url = settings[`social_${key}_url`];
                    const logo = settings[`social_${key}_logo_url`];
                    return (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={key}
                        title={key}
                        className={`flex items-center justify-center w-9 h-9 border border-white/10 text-white/70 transition-colors ${color}`}
                        data-testid={`footer-social-${key}`}
                      >
                        {logo ? (
                          <img src={assetUrl(logo)} alt={key} style={{ width: `${settings.social_logos_size_px || 18}px`, height: `${settings.social_logos_size_px || 18}px` }} className="object-contain" loading="lazy" decoding="async" />
                        ) : (
                          <Icon style={{ width: `${settings.social_logos_size_px || 18}px`, height: `${settings.social_logos_size_px || 18}px` }} />
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {settings.footer_show_carriers && carriers.length > 0 && (
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-3">{lang === "ar" ? "شركات التوصيل" : "Transporteurs"}</div>
              <ul className="space-y-2 text-sm text-white/70">
                {carriers.map((c) => (
                  <li key={c.id} className="flex items-center gap-2">
                    {c.logo_url && <img src={cldUrl(c.logo_url, { w: 120 })} alt={c.name} style={{ height: `${settings.carrier_logos_height_px || 20}px` }} className="w-auto bg-white/5 p-0.5" loading="lazy" decoding="async" />}
                    <span>{c.name}</span>
                    <span className="text-white/40 text-xs">· {lang === "ar" ? c.delay_ar : c.delay_fr}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {settings.footer_show_payments && payments.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-3">
              {lang === "ar" ? "وسائل الدفع" : "Moyens de paiement"}
            </div>
            <div className="flex flex-wrap items-center gap-3" data-testid="payment-badges">
              {payments.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5 border border-white/10 bg-white/[0.02] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white/70">
                  {p.logo_url ? (
                    <img src={cldUrl(p.logo_url, { w: 120 })} alt={p.label_fr} style={{ height: `${settings.payment_logos_height_px || 20}px` }} className="w-auto" loading="lazy" decoding="async" />
                  ) : (
                    <span className="w-5 h-5 rounded-sm bg-white/10 flex items-center justify-center text-[8px]">{p.id.slice(0, 3).toUpperCase()}</span>
                  )}
                  <span>{lang === "ar" ? p.label_ar : p.label_fr}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} {settings.site_name}. {t.footer.rights}.</div>
          {/* Admin access removed from public footer for security. Use the secret URL configured in REACT_APP_ADMIN_PATH. */}
          <button onClick={onAdminClick} aria-hidden="true" tabIndex={-1} className="sr-only" data-testid="admin-access-btn">
            <Lock className="w-3.5 h-3.5" />{t.admin.btn}
          </button>
        </div>
      </div>
    </footer>
  );
}
